// Staff Management System - Data Storage and Utilities
// Handles practice staff, individual orders, cost tracking, and compliance monitoring

import { logger } from './logger';

const STAFF_KEY = 'meetingsync_staff_data';
const STAFF_ORDERS_KEY = 'meetingsync_staff_orders';
const PRACTICE_SETTINGS_KEY = 'meetingsync_practice_settings';

export const StaffStorage = {
  
  // =================== PRACTICE STAFF MANAGEMENT ===================
  
  /**
   * Get all staff members for a specific practice/physician
   * @param {string} physicianId - The healthcare provider's ID
   * @returns {Array} Array of staff members
   */
  getStaffForPhysician(physicianId) {
    try {
      const staffData = this.getAllStaffData();
      return staffData.filter(staff => staff.physicianId === physicianId);
    } catch (error) {
      logger.error('Error getting staff for physician', { error: error.message, physicianId });
      return [];
    }
  },

  /**
   * Add a new staff member to a practice
   * @param {string} physicianId - The healthcare provider's ID
   * @param {Object} staffMember - Staff member details
   * @returns {Object|null} Created staff member or null if failed
   */
  addStaffMember(physicianId, staffMember) {
    try {
      const staffData = this.getAllStaffData();
      const newStaff = {
        id: Date.now().toString(),
        physicianId,
        ...staffMember,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: true,
        orderHistory: [],
        preferences: {
          dietaryRestrictions: staffMember.dietaryRestrictions || [],
          favoriteCuisines: staffMember.favoriteCuisines || [],
          allergens: staffMember.allergens || [],
          notes: staffMember.notes || ''
        }
      };
      
      staffData.push(newStaff);
      localStorage.setItem(STAFF_KEY, JSON.stringify(staffData));
      return newStaff;
    } catch (error) {
      logger.error('Error adding staff member', { error: error.message, physicianId });
      return null;
    }
  },

  /**
   * Update an existing staff member
   * @param {string} staffId - Staff member ID
   * @param {Object} updates - Updates to apply
   * @returns {Object|null} Updated staff member or null if failed
   */
  updateStaffMember(staffId, updates) {
    try {
      const staffData = this.getAllStaffData();
      const staffIndex = staffData.findIndex(s => s.id === staffId);
      
      if (staffIndex >= 0) {
        staffData[staffIndex] = {
          ...staffData[staffIndex],
          ...updates,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem(STAFF_KEY, JSON.stringify(staffData));
        return staffData[staffIndex];
      }
      return null;
    } catch (error) {
      logger.error('Error updating staff member', { error: error.message, staffId });
      return null;
    }
  },

  /**
   * Remove/deactivate a staff member
   * @param {string} staffId - Staff member ID
   * @returns {boolean} Success status
   */
  removeStaffMember(staffId) {
    try {
      const staffData = this.getAllStaffData();
      const staffIndex = staffData.findIndex(s => s.id === staffId);
      
      if (staffIndex >= 0) {
        staffData[staffIndex].isActive = false;
        staffData[staffIndex].deactivatedAt = new Date().toISOString();
        localStorage.setItem(STAFF_KEY, JSON.stringify(staffData));
        return true;
      }
      return false;
    } catch (error) {
      logger.error('Error removing staff member', { error: error.message, staffId });
      return false;
    }
  },

  // =================== STAFF ORDERS & TRACKING ===================

  /**
   * Record staff orders for a specific meeting
   * @param {string} meetingId - Meeting ID
   * @param {Array} staffOrders - Array of staff order objects
   * @returns {boolean} Success status
   */
  recordStaffOrders(meetingId, staffOrders) {
    try {
      const ordersData = this.getAllStaffOrders();
      
      const meetingOrders = {
        id: Date.now().toString(),
        meetingId,
        orders: staffOrders.map(order => ({
          staffId: order.staffId,
          staffName: order.staffName,
          items: order.items || [],
          individualCost: order.individualCost || 0,
          specialRequests: order.specialRequests || '',
          attended: order.attended !== false, // Default to true
          orderTimestamp: new Date().toISOString()
        })),
        totalStaffCost: staffOrders.reduce((sum, order) => sum + (order.individualCost || 0), 0),
        totalAttendees: staffOrders.length,
        recordedAt: new Date().toISOString()
      };
      
      ordersData.push(meetingOrders);
      localStorage.setItem(STAFF_ORDERS_KEY, JSON.stringify(ordersData));
      
      // Update staff order history
      this.updateStaffOrderHistory(staffOrders);

      return true;
    } catch (error) {
      logger.error('Error recording staff orders', { error: error.message, meetingId });
      return false;
    }
  },

  /**
   * Get staff orders for a specific meeting
   * @param {string} meetingId - Meeting ID
   * @returns {Object|null} Meeting staff orders or null
   */
  getStaffOrdersForMeeting(meetingId) {
    try {
      const ordersData = this.getAllStaffOrders();
      return ordersData.find(orders => orders.meetingId === meetingId) || null;
    } catch (error) {
      logger.error('Error getting staff orders for meeting', { error: error.message, meetingId });
      return null;
    }
  },

  /**
   * Update individual staff member order history
   * @param {Array} staffOrders - Array of staff orders
   */
  updateStaffOrderHistory(staffOrders) {
    try {
      const staffData = this.getAllStaffData();
      
      staffOrders.forEach(order => {
        const staffIndex = staffData.findIndex(s => s.id === order.staffId);
        if (staffIndex >= 0) {
          if (!staffData[staffIndex].orderHistory) {
            staffData[staffIndex].orderHistory = [];
          }
          
          staffData[staffIndex].orderHistory.push({
            orderedAt: new Date().toISOString(),
            items: order.items,
            cost: order.individualCost,
            meetingId: order.meetingId
          });
          
          // Keep only last 50 orders per staff member
          if (staffData[staffIndex].orderHistory.length > 50) {
            staffData[staffIndex].orderHistory = staffData[staffIndex].orderHistory.slice(-50);
          }
        }
      });

      localStorage.setItem(STAFF_KEY, JSON.stringify(staffData));
    } catch (error) {
      logger.error('Error updating staff order history', { error: error.message });
    }
  },

  // =================== COST CALCULATION & COMPLIANCE ===================

  /**
   * Calculate meeting costs with staff breakdown
   * @param {string} meetingId - Meeting ID
   * @returns {Object} Cost breakdown object
   */
  calculateMeetingCosts(meetingId) {
    try {
      const staffOrders = this.getStaffOrdersForMeeting(meetingId);
      if (!staffOrders) {
        return {
          physicianCost: 0,
          staffCost: 0,
          totalCost: 0,
          attendeeCount: 1, // Just physician
          avgCostPerPerson: 0,
          staffAttendees: []
        };
      }

      const staffCost = staffOrders.totalStaffCost;
      const attendeeCount = staffOrders.totalAttendees + 1; // +1 for physician
      
      // Get physician order cost (would need to integrate with existing meeting data)
      const physicianCost = this.getPhysicianOrderCost(meetingId);
      const totalCost = physicianCost + staffCost;
      
      return {
        physicianCost,
        staffCost,
        totalCost,
        attendeeCount,
        avgCostPerPerson: totalCost / attendeeCount,
        staffAttendees: staffOrders.orders,
        breakdown: {
          physician: { cost: physicianCost, count: 1 },
          staff: { cost: staffCost, count: staffOrders.totalAttendees }
        }
      };
    } catch (error) {
      logger.error('Error calculating meeting costs', { error: error.message, meetingId });
      return { physicianCost: 0, staffCost: 0, totalCost: 0, attendeeCount: 1, avgCostPerPerson: 0, staffAttendees: [] };
    }
  },

  /**
   * Generate attendee list for expense reporting
   * @param {string} meetingId - Meeting ID
   * @param {Object} meetingDetails - Additional meeting details
   * @returns {Object} Formatted attendee list for reporting
   */
  generateAttendeeList(meetingId, meetingDetails = {}) {
    try {
      const staffOrders = this.getStaffOrdersForMeeting(meetingId);
      const costs = this.calculateMeetingCosts(meetingId);
      
      return {
        meetingId,
        meetingDate: meetingDetails.date || new Date().toISOString(),
        physician: {
          name: meetingDetails.physicianName || 'Healthcare Provider',
          title: meetingDetails.physicianTitle || '',
          practice: meetingDetails.practice || '',
          cost: costs.physicianCost
        },
        staffAttendees: staffOrders ? staffOrders.orders.map(order => ({
          name: order.staffName,
          role: order.role || 'Staff Member',
          items: order.items,
          cost: order.individualCost,
          attended: order.attended
        })) : [],
        summary: {
          totalAttendees: costs.attendeeCount,
          totalCost: costs.totalCost,
          avgCostPerPerson: costs.avgCostPerPerson,
          physicianCost: costs.physicianCost,
          staffCost: costs.staffCost
        },
        generatedAt: new Date().toISOString(),
        generatedFor: 'expense-reimbursement'
      };
    } catch (error) {
      logger.error('Error generating attendee list', { error: error.message, meetingId });
      return null;
    }
  },

  // =================== ANALYTICS & REPORTING ===================

  /**
   * Get staff analytics for a physician's practice
   * @param {string} physicianId - Physician ID
   * @param {number} daysBack - Number of days to analyze (default 30)
   * @returns {Object} Analytics data
   */
  getStaffAnalytics(physicianId, daysBack = 30) {
    try {
      const staff = this.getStaffForPhysician(physicianId);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);
      
      const analytics = {
        totalStaff: staff.filter(s => s.isActive).length,
        avgStaffPerMeeting: 0,
        totalStaffCost: 0,
        mostActiveStaff: [],
        dietaryRestrictionsSummary: {},
        topOrderedItems: {}
      };
      
      // Analyze staff participation and costs
      const ordersData = this.getAllStaffOrders();
      const recentOrders = ordersData.filter(order => 
        new Date(order.recordedAt) >= cutoffDate
      );
      
      if (recentOrders.length > 0) {
        const totalStaffInMeetings = recentOrders.reduce((sum, order) => sum + order.totalAttendees, 0);
        analytics.avgStaffPerMeeting = totalStaffInMeetings / recentOrders.length;
        analytics.totalStaffCost = recentOrders.reduce((sum, order) => sum + order.totalStaffCost, 0);
      }

      return analytics;
    } catch (error) {
      logger.error('Error getting staff analytics', { error: error.message, physicianId, daysBack });
      return { totalStaff: 0, avgStaffPerMeeting: 0, totalStaffCost: 0, mostActiveStaff: [], dietaryRestrictionsSummary: {}, topOrderedItems: {} };
    }
  },

  // =================== UTILITY METHODS ===================

  /**
   * Get all staff data
   * @returns {Array} All staff members
   */
  getAllStaffData() {
    try {
      const data = localStorage.getItem(STAFF_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      logger.error('Error getting all staff data', { error: error.message });
      return [];
    }
  },

  /**
   * Get all staff orders
   * @returns {Array} All staff orders
   */
  getAllStaffOrders() {
    try {
      const data = localStorage.getItem(STAFF_ORDERS_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      logger.error('Error getting all staff orders', { error: error.message });
      return [];
    }
  },

  /**
   * Get staff member by ID
   * @param {string} staffId - Staff member ID
   * @returns {Object|null} Staff member object or null
   */
  getStaffMemberById(staffId) {
    try {
      const staffData = this.getAllStaffData();
      return staffData.find(staff => staff.id === staffId) || null;
    } catch (error) {
      logger.error('Error getting staff member by ID', { error: error.message, staffId });
      return null;
    }
  },

  /**
   * Backup all staff data
   * @returns {Object} Backup data object
   */
  backupData() {
    try {
      return {
        staff: this.getAllStaffData(),
        orders: this.getAllStaffOrders(),
        timestamp: new Date().toISOString(),
        version: '1.0'
      };
    } catch (error) {
      logger.error('Error creating backup', { error: error.message });
      return null;
    }
  },

  /**
   * Restore staff data from backup
   * @param {Object} backupData - Backup data object
   * @returns {boolean} Success status
   */
  restoreData(backupData) {
    try {
      if (!backupData || !backupData.staff || !backupData.orders) {
        logger.error('Invalid backup data');
        return false;
      }

      localStorage.setItem(STAFF_KEY, JSON.stringify(backupData.staff));
      localStorage.setItem(STAFF_ORDERS_KEY, JSON.stringify(backupData.orders));

      return true;
    } catch (error) {
      logger.error('Error restoring backup', { error: error.message });
      return false;
    }
  },

  /**
   * Get physician order cost (placeholder - integrate with existing meeting storage)
   * @param {string} meetingId - Meeting ID
   * @returns {number} Physician order cost
   */
  getPhysicianOrderCost(meetingId) {
    // TODO: Integrate with existing MeetingStorage to get physician order cost
    // For now, return 0 as placeholder
    return 0;
  },

  /**
   * Export attendee list to CSV format
   * @param {Object} attendeeList - Attendee list data
   * @returns {string} CSV formatted string
   */
  exportToCSV(attendeeList) {
    if (!attendeeList) return '';
    
    const headers = ['Name', 'Role', 'Items Ordered', 'Individual Cost', 'Attended'];
    const rows = [];
    
    // Add physician row
    rows.push([
      attendeeList.physician.name,
      attendeeList.physician.title || 'Healthcare Provider',
      'Physician Order',
      attendeeList.physician.cost.toFixed(2),
      'Yes'
    ]);
    
    // Add staff rows
    attendeeList.staffAttendees.forEach(staff => {
      rows.push([
        staff.name,
        staff.role,
        staff.items.join('; '),
        staff.cost.toFixed(2),
        staff.attended ? 'Yes' : 'No'
      ]);
    });
    
    // Convert to CSV
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    return csvContent;
  },

  /**
   * Clear all staff data (for testing/reset purposes)
   */
  clearAllData() {
    localStorage.removeItem(STAFF_KEY);
    localStorage.removeItem(STAFF_ORDERS_KEY);
    localStorage.removeItem(PRACTICE_SETTINGS_KEY);
  }
};

// Export utility functions for easy access
export const StaffUtils = {
  /**
   * Format cost for display
   * @param {number} cost - Cost amount
   * @returns {string} Formatted cost string
   */
  formatCost(cost) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cost);
  },

  /**
   * Generate staff member ID
   * @returns {string} Unique staff ID
   */
  generateStaffId() {
    return `staff_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  /**
   * Validate staff member data
   * @param {Object} staffData - Staff member data to validate
   * @returns {Object} Validation result
   */
  validateStaffData(staffData) {
    const errors = [];
    
    if (!staffData.name || staffData.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }
    
    if (!staffData.role || staffData.role.trim().length < 2) {
      errors.push('Role is required');
    }
    
    if (staffData.email && !this.isValidEmail(staffData.email)) {
      errors.push('Invalid email format');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Check if email is valid
   * @param {string} email - Email to validate
   * @returns {boolean} Is email valid
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
};